<template>
  <div class="sales-analytic">
    <div class="d-flex mb-3 justify-content-between align-items-center">
      <h4 class="mb-0 pb-0 pl-3 pt-4">Аналитика продаж</h4>

      <div class="d-flex justify-content-flex-end align-items-center">
        <!-- Общие фильтры -->
        <div class="row align-items-center">
          <div class="col-auto">
            <label class="form-label small-form-label">Период:</label>
            <div class="input-group">
              <b-form-input 
                type="date" 
                size="sm"
                style="height: 36px;"
                v-model="filters.period_start" 
                :disabled="loading"
              ></b-form-input>
              <b-form-input 
                type="date" 
                size="sm"
                style="height: 36px;"
                v-model="filters.period_end" 
                :disabled="loading"
              ></b-form-input>
            </div>
          </div>

          <!-- Фильтр по отделам -->
          <div class="col-auto corrected-multiselect__tags small-tags" style="height: 65px;">
            <label class="form-label small-form-label">Отдел:</label>
            <div 
              style="position: relative; margin-top: 3px" 
              :style="{ width: filters.departments.length > 1 ? '180px' : '130px' }"
            >
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
                <multiselect
                  key="departments_selector"
                  v-model="filters.departments"
                  select-label=""
                  :options="departmentOptions"
                  :multiple="true"
                  placeholder="Отделы"
                  label="text"
                  track-by="value"
                  :disabled="loading"
                  :hide-selected="true"
                  style="min-width: 100%;"
                >
                  <span slot="noResult">Ничего не найдено. Попробуйте снова...</span>
                  <span slot="noOptions">Список пуст</span> <!-- Custom text for an empty list -->
                </multiselect>
              </div>
            </div>
          </div>
          
          <div class="col-auto corrected-multiselect__tags small-tags" style="height: 65px;">
            <label class="form-label small-form-label">Услуга:</label>
            <div 
              style="position: relative; margin-top: 3px"
              :style="{ width: filters.service_ids.length > 1 ? '200px' : '120px' }"
            >
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
                <tsoft-select-of-properties
                  :key="`set_segments_transport_company`"
                  :placeholder="`Услуги`"
                  :reference_parent_id="21"
                  :needNullElement="false"
                  :needMultipleSelect="true"
                  :defaultSelectName="``"
                  :excludeArray="[66]"
                  :selectedArray="filters.service_ids"
                  @set="setServiceIds"
                ></tsoft-select-of-properties>
              </div>
            </div>
          </div>
          <div class="col-auto">
            <label class="form-label small-form-label">&nbsp;</label>
            <div>
              <b-button
                size="sm"
                variant="outline-secondary"
                style="height: 36px;"
                @click="updateFilters"
                :disabled="loading"
              >
                Применить
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Компоненты аналитики -->
    <div class="mb-4">
      <RequestsAnalytic 
        :filters="activeFilters"
        :loading="loadingStates.requests"
        @update-loading="setRequestsLoading"
      />
    </div>
    
    <div class="mb-4">
      <FeedbackAnalytic 
        :filters="activeFilters"
        :loading="loadingStates.feedback"
        @update-loading="setFeedbackLoading"
      />
    </div>
    
    <div class="mb-4">
      <ManagersAnalytic 
        :filters="activeFilters"
        :loading="loadingStates.managers"
        @update-loading="setManagersLoading"
      />
    </div>
    
    <div class="mb-4" v-if="shouldShowCountriesAnalytic">
      <CountriesAnalytic 
        :filters="activeFilters"
        :loading="loadingStates.countries"
        @update-loading="setCountriesLoading"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import RequestsAnalytic from '@/components/salesAnalytic/RequestsAnalytic.vue';
import FeedbackAnalytic from '@/components/salesAnalytic/FeedbackAnalytic.vue';
import CountriesAnalytic from '@/components/salesAnalytic/CountriesAnalytic.vue';
import ManagersAnalytic from '@/components/salesAnalytic/ManagersAnalytic.vue';

export default {
  name: "SalesAnalytic",

  components: {
    RequestsAnalytic,
    FeedbackAnalytic,
    CountriesAnalytic,
    ManagersAnalytic
  },

  data() {
    return {
      loadingStates: {
        requests: false,
        feedback: false,
        countries: false,
        managers: false
      },
      filters: {
        period_start: '',
        period_end: '',
        service_ids: [23], // По умолчанию ТЭО
        departments: [{ text: 'Агенты', value: 'agents' }] // По умолчанию Агенты
      },
      // Активные фильтры, которые передаются в дочерние компоненты
      activeFilters: {
        period_start: '',
        period_end: '',
        service_ids: [23], // По умолчанию ТЭО
        departments: [{ text: 'Агенты', value: 'agents' }] // По умолчанию Агенты
      },
      departmentOptions: [
        { text: 'Агенты', value: 'agents' },
        { text: 'Продажи', value: 'sales' },
        { text: 'ТО', value: 'TO' }
      ],
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser"]),

    isSalesManagerAccount() {
      return this.currentUser.role.includes('sales-manager');
    },

    // Общее состояние загрузки для кнопки "Применить"
    loading() {
      return Object.values(this.loadingStates).some(state => state);
    },

    shouldShowCountriesAnalytic() {
      return this.activeFilters.service_ids.includes(23);
    },
  },

  mounted() {
    this.initializeFilters();
  },

  watch: {
    shouldShowCountriesAnalytic(newValue) {
      // Если блок стран скрывается, сбрасываем состояние загрузки
      if (!newValue) {
        this.loadingStates.countries = false;
      }
    }
  },

  methods: {
    initializeFilters() {
      // Устанавливаем период по умолчанию (неделя от сегодняшней даты)
      const now = new Date();
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      
      this.filters.period_start = weekAgo.toISOString().split('T')[0];
      this.filters.period_end = now.toISOString().split('T')[0];
      
      // Инициализируем активные фильтры
      this.activeFilters.period_start = this.filters.period_start;
      this.activeFilters.period_end = this.filters.period_end;
      this.activeFilters.service_ids = [...this.filters.service_ids];
      this.activeFilters.departments = this.filters.departments.map(dept => dept.value);
      
      // Автоматически загружаем данные при первой загрузке
      this.updateFilters();
    },

    updateFilters() {
      // Обновляем активные фильтры только при клике на кнопку "Обновить"
      this.activeFilters = {
        period_start: this.filters.period_start,
        period_end: this.filters.period_end,
        service_ids: [...this.filters.service_ids],
        departments: this.filters.departments.map(dept => dept.value) // Преобразуем объекты в значения
      };
    },

    setServiceIds(value) {
      // Сохраняем изменения только в filters, не в activeFilters
      this.filters.service_ids = value;
    },

    setRequestsLoading(value) {
      this.loadingStates.requests = value;
    },

    setFeedbackLoading(value) {
      this.loadingStates.feedback = value;
    },

    setCountriesLoading(value) {
      this.loadingStates.countries = value;
    },

    setManagersLoading(value) {
      this.loadingStates.managers = value;
    },
  }
};
</script>

<style scoped>
.sales-analytic {
  padding-bottom: 1rem;
}

.page-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.input-group {
  min-width: 280px;
}

.input-group .form-control:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-group .form-control:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 0;
}

.input-group .form-control:not(:first-child):not(:last-child) {
  border-radius: 0;
  border-left: 0;
  border-right: 0;
}

.btn-outline-secondary {
  border-color: #ced4da;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
}
.small-form-label {
  margin-bottom: 0;
  font-size: 11px;
}
</style>